import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { dataHora, nomeDoContato, plural, rotulo } from '../../formato';
import { useSessao } from '../../sessao';
import { ACAO_DO_AGENTE, SITUACAO_DO_AGENTE, degrauDe } from '../../situacoes';
import {
  Area, BarraDeAvanco, Campo, Carregando, Dialogo, ErroDaApi, RespostaDaAcao, SegurarParaConfirmar,
  SeloDaEscada, Vazio,
} from '../../ui';
import { PAPEL_DA_IA, reais } from './crm';
import { faltaPara, Rodape } from './JanelasDaConversa';
import './agente.css';

/**
 * O agente de IA do Comercial — onda 552 (Plano 2 · Comercial, Etapa 1,
 * Tarefa 24; spec R02 §7.5–7.6). `/comercial/agente`, no Design Moderno.
 *
 * Quatro abas, cada uma no endereço (`?aba=`):
 *
 *  · **Situação** — ligado ou não, a fase configurada e a que vale agora, o
 *    gasto do mês contra o teto (e por papel) e o acerto do copiloto nas duas
 *    últimas semanas («N de cada 10 sem correção», a régua da fase 2). Quem
 *    configura o agente (a diretoria) muda a fase, o aviso de atendimento
 *    automatizado, o link da privacidade e «ler propostas», com motivo; o
 *    desligar é de segurar (`SegurarParaConfirmar`), com o motivo também.
 *  · **Decisões** — o que o agente decidiu em cada conversa que a pessoa vê,
 *    e o que a pessoa fez com a sugestão. A decisão que pode citar valor
 *    mostra a resposta e o motivo só a quem tem o Comercial (M-10).
 *  · **Base** — os artigos vigentes que o agente cita; quem aprova a base
 *    grava o artigo novo ou a versão nova.
 *  · **Lacunas** — o que a base não respondeu, para quem aprova a base
 *    escrever o artigo (a aba só aparece para ela).
 *
 * Nenhum código de permissão na tela (P-TXT-03); a recusa vem da API, em
 * três partes.
 */

type Aba = 'situacao' | 'decisoes' | 'base' | 'lacunas';

const ABAS: { chave: Aba; texto: string }[] = [
  { chave: 'situacao', texto: 'Situação' },
  { chave: 'decisoes', texto: 'Decisões' },
  { chave: 'base', texto: 'Base' },
  { chave: 'lacunas', texto: 'Lacunas' },
];

const FASES: { fase: number; titulo: string; diz: string }[] = [
  { fase: 1, titulo: 'Copiloto', diz: 'o agente só sugere; uma pessoa envia' },
  { fase: 2, titulo: 'Fora do horário', diz: 'responde sozinho pelo WhatsApp fora do horário comercial; no horário, sugere — o acompanhamento das propostas sai sozinho; ele e o retorno combinado saem também pelo e-mail e pelo site' },
  { fase: 3, titulo: 'Sempre', diz: 'responde sozinho pelo WhatsApp e pelo e-mail' },
];

const PAPEL = PAPEL_DA_IA;

export function Agente() {
  const { pode } = useSessao();
  const [busca, setBusca] = useSearchParams();
  const editaBase = pode('base.editar');
  const abas = ABAS.filter((a) => a.chave !== 'lacunas' || editaBase);
  const aba: Aba = abas.find((a) => a.chave === busca.get('aba'))?.chave ?? 'situacao';
  const ir = (a: Aba) => {
    const b = new URLSearchParams(busca);
    if (a === 'situacao') b.delete('aba'); else b.set('aba', a);
    setBusca(b, { replace: true });
  };

  return (
    <div className="agt">
      <header className="cabeca">
        <h1>Agente de IA</h1>
        <p className="sub">O copiloto do atendimento: o que ele sugere, quanto gasta e o que a base de conhecimento responde.</p>
      </header>
      <div className="abas agt-abas" role="tablist" aria-label="Agente de IA">
        {abas.map((a) => (
          <button key={a.chave} type="button" role="tab" aria-selected={aba === a.chave}
            className={aba === a.chave ? 'ativa' : undefined} onClick={() => ir(a.chave)}>
            {a.texto}
          </button>
        ))}
      </div>
      <section className="agt-painel" role="tabpanel" aria-label={abas.find((a) => a.chave === aba)?.texto}>
        {aba === 'situacao' && <AbaSituacao />}
        {aba === 'decisoes' && <AbaDecisoes />}
        {aba === 'base' && <AbaBase editaBase={editaBase} />}
        {aba === 'lacunas' && <AbaLacunas />}
      </section>
    </div>
  );
}

/** Carrega uma rota da API e devolve o dado, o erro e o «de novo». */
function useCarga<T>(rota: string) {
  const [dado, setDado] = useState<T | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const vez = useRef(0);
  const carregar = useCallback(async () => {
    const n = ++vez.current;
    try {
      const r = await api<T>(rota);
      if (n !== vez.current) return;
      setDado(r);
      setErro(null);
    } catch (e) {
      if (n === vez.current) setErro(e);
    }
  }, [rota]);
  useEffect(() => { void carregar(); }, [carregar]);
  return { dado, setDado, erro, carregar };
}

function Cartao({ titulo, frase, children, acoes }: { titulo: string; frase?: ReactNode; children: ReactNode; acoes?: ReactNode }) {
  return (
    <div className="cartao agt-cartao">
      <div className="cartao-cabeca">
        <h2>{titulo}</h2>
        {acoes}
      </div>
      {frase && <p className="agt-frase">{frase}</p>}
      {children}
    </div>
  );
}

// ── Situação ───────────────────────────────────────────────────────────

interface Situacao {
  ligado: boolean; fase: number; faseEfetiva: number; ligadoEm: string | null;
  avisoAutomatizado: boolean; linkPrivacidade: string | null; lePropostas: boolean;
  /** D3 (Humberto, 01/11/2026): o gasto da IA é só da diretoria — sem ela, as chaves nem vêm. */
  veGasto: boolean; noTeto: boolean;
  gastoMes?: number; teto?: number; cortePercentual?: number; tetoAvisadoEm?: string | null;
  porPapel?: { papel: string; gasto: number }[];
  sugestoes: { resolvidas: number; semCorrecao: number };
  podeConfigurar: boolean;
}

/** A situação do agente em uma palavra (o selo). */
function situacaoDoAgente(s: Situacao): string {
  if (!s.ligado) return 'desligado';
  if (s.faseEfetiva === 0) return 'no_corte';
  if (s.noTeto && s.fase > 1) return 'no_teto';
  return 'ligado';
}

/** A fase que vale agora, dita como se fala. */
function faseQueVale(s: Situacao): string {
  if (!s.ligado) return 'Nenhuma: o agente está desligado.';
  if (s.faseEfetiva === 0) return 'Nenhuma: o gasto do mês chegou ao corte, e o agente para até o mês virar ou o teto subir.';
  const f = FASES.find((x) => x.fase === s.faseEfetiva);
  if (s.faseEfetiva < s.fase) {
    return s.noTeto
      ? `${s.faseEfetiva} · ${f?.titulo}: o gasto chegou ao teto, e o agente volta a só sugerir.`
      : `${s.faseEfetiva} · ${f?.titulo}: agora é horário comercial, e no horário o agente só sugere (o acompanhamento das propostas sai sozinho).`;
  }
  return `${s.faseEfetiva} · ${f?.titulo}: ${f?.diz}.`;
}

/** «9 de cada 10 sem correção» — a régua do copiloto (§7.5). */
function acerto(s: Situacao['sugestoes']): string {
  if (!s.resolvidas) {
    return 'Nenhuma sugestão resolvida nas duas últimas semanas: o acerto conta as que saíram sem correção, de cada 10.';
  }
  return `${Math.round((10 * s.semCorrecao) / s.resolvidas)} de cada 10 sem correção nas duas últimas semanas `
    + `(${plural(s.semCorrecao, 'enviada como estava', 'enviadas como estavam')}, de `
    + `${plural(s.resolvidas, 'resolvida', 'resolvidas')}).`;
}

function AbaSituacao() {
  const { dado: s, setDado, erro, carregar } = useCarga<Situacao>('/comercial/agente/situacao');
  if (erro && !s) return <ErroDaApi erro={erro} />;
  if (!s) return <Carregando texto="Carregando a situação do agente" />;
  return (
    <>
      <Cartao titulo="Agora" acoes={<SeloDaEscada degrau={degrauDe(SITUACAO_DO_AGENTE, situacaoDoAgente(s))} />}>
        <div className="agt-corpo">
          <dl className="agt-numeros">
            <div>
              <dt>Fase configurada</dt>
              <dd>{s.fase} · {FASES.find((f) => f.fase === s.fase)?.titulo}</dd>
            </div>
            <div>
              <dt>Fase que vale agora</dt>
              <dd>{faseQueVale(s)}</dd>
            </div>
            <div>
              <dt>Acerto do copiloto</dt>
              <dd>{acerto(s.sugestoes)}</dd>
            </div>
          </dl>
        </div>
      </Cartao>

      {s.veGasto && <Gasto s={s} />}

      <Configuracao s={s} aoMudar={(n) => { setDado(n); void carregar(); }} />
    </>
  );
}

/** O gasto do mês: só a diretoria o recebe (D3). */
function Gasto({ s }: { s: Situacao }) {
  const gastoMes = s.gastoMes ?? 0;
  const teto = s.teto ?? 0;
  const porPapel = s.porPapel ?? [];
  const pct = teto > 0 ? (100 * gastoMes) / teto : 0;
  return (
      <Cartao titulo="Gasto do mês" frase={`Teto de ${reais(teto)}: a 80 % a diretoria é avisada; no teto o agente volta a só sugerir; a ${s.cortePercentual} % ele para.`}>
        <div className="agt-corpo">
          <p className="agt-texto">
            {reais(gastoMes)} de {reais(teto)}
            {s.tetoAvisadoEm && <span className="agt-aux">A diretoria foi avisada dos 80 % em {dataHora(s.tetoAvisadoEm)}.</span>}
          </p>
          <BarraDeAvanco pct={pct} rotulo="Gasto do mês contra o teto" />
          {porPapel.length ? (
            <>
              {/* A parte de cada papel no gasto do mês — e não no teto (m-4 da revisão). */}
              <p className="agt-texto aux">Por papel, a parte de cada um no gasto do mês:</p>
              <ul className="agt-papeis" aria-label="Parte de cada papel no gasto do mês">
                {porPapel.map((p) => (
                  <li key={p.papel}>
                    <span>{PAPEL[p.papel] ?? rotulo(p.papel)} <span className="agt-aux">{reais(p.gasto)}</span></span>
                    <BarraDeAvanco pct={gastoMes > 0 ? (100 * p.gasto) / gastoMes : 0}
                      rotulo={`Parte de ${PAPEL[p.papel] ?? p.papel} no gasto do mês`} />
                  </li>
                ))}
              </ul>
            </>
          ) : <p className="agt-texto aux">Nenhum gasto com o provedor de IA neste mês.</p>}
        </div>
      </Cartao>
  );
}

function Configuracao({ s, aoMudar }: { s: Situacao; aoMudar(n: Situacao): void }) {
  const [fase, setFase] = useState(s.fase);
  const [aviso, setAviso] = useState(s.avisoAutomatizado);
  const [le, setLe] = useState(s.lePropostas);
  const [link, setLink] = useState(s.linkPrivacidade ?? '');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  useEffect(() => {
    setFase(s.fase); setAviso(s.avisoAutomatizado); setLe(s.lePropostas); setLink(s.linkPrivacidade ?? '');
  }, [s]);

  if (!s.podeConfigurar) {
    return (
      <Cartao titulo="Configuração">
        <p className="agt-frase">
          Só quem configura o agente (a diretoria) liga, desliga e muda a fase; aqui você a consulta.
          {` Aviso de atendimento automatizado: ${s.avisoAutomatizado ? 'ligado' : 'desligado'}.`}
          {` O agente ${s.lePropostas ? 'lê' : 'não lê'} as mensagens marcadas como comerciais.`}
        </p>
      </Cartao>
    );
  }

  const mudou = fase !== s.fase || aviso !== s.avisoAutomatizado || le !== s.lePropostas
    || link.trim() !== (s.linkPrivacidade ?? '');
  const faltam = 10 - motivo.trim().length;
  const gravar = async (extra: { ligado?: boolean } = {}) => {
    if (ocupado) return;
    setOcupado(true);
    setErro(null);
    setOk(null);
    try {
      const n = await api<Situacao>('/comercial/agente/config', {
        metodo: 'PATCH',
        corpo: {
          ...extra, fase, avisoAutomatizado: aviso, lePropostas: le,
          linkPrivacidade: link.trim() || null, motivo: motivo.trim(),
        },
      });
      setMotivo('');
      setOk(extra.ligado === false ? 'O agente foi desligado.' : extra.ligado ? 'O agente foi ligado.' : 'A configuração foi gravada.');
      aoMudar(n);
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };
  const falta = faltaPara('gravar', [
    ...(mudou ? [] : ['mudar algo']),
    ...(faltam > 0 ? ['o motivo, com 10 caracteres ou mais'] : []),
  ]);

  return (
    <Cartao titulo="Configuração" frase="Cada mudança vai à trilha com o motivo.">
      <div className="agt-corpo">
        <div className="agt-grupo">
          <span className="agt-grupo-rotulo" id="agt-fase">Fase</span>
          <div className="agt-opcoes" role="radiogroup" aria-labelledby="agt-fase">
            {FASES.map((f) => (
              <label key={f.fase} className="agt-marca">
                <input type="radio" name="fase-do-agente" checked={fase === f.fase} onChange={() => setFase(f.fase)} />
                <span>{f.fase} · {f.titulo} <span className="agt-aux">{f.diz}</span></span>
              </label>
            ))}
          </div>
        </div>
        <div className="agt-opcoes">
          <label className="agt-marca">
            <input type="checkbox" checked={aviso} onChange={(e) => setAviso(e.target.checked)} />
            <span>Avisar no primeiro contato que o atendimento é automatizado</span>
          </label>
          <label className="agt-marca">
            <input type="checkbox" checked={le} onChange={(e) => setLe(e.target.checked)} />
            <span>O agente lê as mensagens marcadas como comerciais (as propostas)
              <span className="agt-aux">O resumo da passagem nunca as usa.</span></span>
          </label>
        </div>
        <Campo rotulo="Link da política de privacidade" value={link} maxLength={500} inputMode="url"
          placeholder="https://" onChange={(e) => setLink(e.target.value)}
          dica="Sem o link, o agente não sugere o primeiro contato." />
        <Area rotulo="Motivo" value={motivo} rows={2} maxLength={500} onChange={(e) => setMotivo(e.target.value)}
          dica={faltam > 0 ? `Fica na trilha. Faltam ${plural(faltam, 'caractere', 'caracteres')}.` : 'Fica na trilha.'} />
        <RespostaDaAcao erro={erro} ok={ok} />
      </div>
      <div className="agt-acoes">
        {s.ligado ? (
          <SegurarParaConfirmar texto="Segure para desligar o agente" desabilitado={faltam > 0 || ocupado}
            aoConfirmar={() => void gravar({ ligado: false })} />
        ) : (
          <button type="button" className="botao secundario" aria-disabled={faltam > 0 || ocupado}
            onClick={() => { if (faltam <= 0) void gravar({ ligado: true }); }}>
            Ligar o agente
          </button>
        )}
        {falta && <span className="o-que-falta">{falta}</span>}
        <button type="button" className="botao" aria-disabled={falta !== null || ocupado}
          onClick={() => { if (falta === null) void gravar(); }}>
          {ocupado ? 'Gravar…' : 'Gravar'}
        </button>
      </div>
    </Cartao>
  );
}

// ── Decisões ───────────────────────────────────────────────────────────

interface Decisao {
  id: string; conversaId: string; contato: string; iniciadaEm: string; concluidaEm: string | null;
  acao: string | null; intencao: string | null; tom: string | null; reescrita: boolean; oculta: boolean;
  respostaFinal: string | null; motivo: string | null;
  travasReprovadas: { trava: string; rodada: number; motivo: string | null }[] | null;
  ferramentas: string[] | null;
  resolucao: { acao: 'enviada' | 'editada' | 'descartada'; por: string; em: string } | null;
}

const RESOLUCAO: Record<string, string> = {
  enviada: 'Enviada sem correção', editada: 'Editada e enviada', descartada: 'Descartada',
};

const TRAVA: Record<string, string> = {
  sem_valor: 'sem valor', sem_promessa: 'sem promessa', so_deste_cliente: 'só deste cliente',
  fonte_tecnica: 'fonte técnica', ritmo: 'ritmo', verdade: 'verdade', formato: 'formato',
};

function AbaDecisoes() {
  const { dado, erro } = useCarga<Decisao[]>('/comercial/agente/decisoes?limite=100');
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando as decisões" />;
  return (
    <Cartao titulo="Decisões" frase="As 100 mais novas das conversas que você vê, da mais nova para a mais velha.">
      {!dado.length ? <Vazio>O agente ainda não decidiu nada nas conversas que você vê.</Vazio> : (
        <ul className="agt-linhas">
          {dado.map((d) => (
            <li key={d.id}>
              <div className="agt-linha-cabeca">
                <SeloDaEscada degrau={degrauDe(ACAO_DO_AGENTE, d.acao ?? 'aberta')} />
                <strong><Link to={`/comercial/conversa/${d.conversaId}`}>{nomeDoContato(d.contato)}</Link></strong>
                <span className="agt-aux">{dataHora(d.concluidaEm ?? d.iniciadaEm)}</span>
                {d.intencao && <span className="agt-aux">Intenção: {rotulo(d.intencao)}</span>}
                {d.resolucao && <span className="agt-aux">{RESOLUCAO[d.resolucao.acao]} por {d.resolucao.por}</span>}
              </div>
              {d.oculta ? (
                <p className="agt-texto aux">
                  A resposta e o motivo desta decisão podem citar valores ou propostas, e são de quem cuida do comercial.
                </p>
              ) : (
                <>
                  {d.respostaFinal && <p className="agt-texto">{d.respostaFinal}</p>}
                  {d.motivo && <p className="agt-texto aux">Motivo: {d.motivo}</p>}
                  {!!d.travasReprovadas?.length && (
                    <p className="agt-texto aux">
                      Travas que reprovaram: {d.travasReprovadas.map((t) => `${TRAVA[t.trava] ?? t.trava}${t.rodada > 1 ? ' (na reescrita)' : ''}`).join(', ')}.
                    </p>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

// ── Base e lacunas ─────────────────────────────────────────────────────

interface Artigo {
  id: string; titulo: string; texto: string; tags: string[]; versao: number; vigente: boolean;
  aprovadoPorNome: string | null; aprovadoEm: string | null;
}

interface Lacuna {
  id: string; pergunta: string; conversaId: string; criadaEm: string; artigoId: string | null; artigoTitulo: string | null;
}

type Escrita = { substitui?: Artigo; lacunas?: Lacuna[] } | null;

function AbaBase({ editaBase }: { editaBase: boolean }) {
  const { dado, erro, carregar } = useCarga<Artigo[]>('/comercial/base');
  const [escrita, setEscrita] = useState<Escrita>(null);
  const [ok, setOk] = useState<string | null>(null);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando a base de conhecimento" />;
  return (
    <Cartao titulo="Base de conhecimento"
      frase={editaBase
        ? 'Os artigos que o agente cita. Cada versão nova fica com quem a aprovou, e a anterior sai de uso.'
        : 'Os artigos que o agente cita. Quem aprova a base (a diretoria) grava os artigos.'}
      acoes={editaBase ? <button type="button" className="botao" onClick={() => setEscrita({})}>Novo artigo</button> : undefined}>
      <div className="agt-corpo"><RespostaDaAcao erro={null} ok={ok} /></div>
      {!dado.length ? <Vazio>A base ainda não tem artigo: sem fonte, o agente passa a dúvida técnica a uma pessoa.</Vazio> : (
        <ul className="agt-linhas">
          {dado.map((a) => (
            <li key={a.id}>
              <div className="agt-linha-cabeca">
                <strong>{a.titulo}</strong>
                <span className="agt-aux">Versão {a.versao}{a.aprovadoPorNome ? ` · aprovada por ${a.aprovadoPorNome}` : ''}{a.aprovadoEm ? ` em ${dataHora(a.aprovadoEm)}` : ''}</span>
                {editaBase && (
                  <button type="button" className="botao discreto" onClick={() => setEscrita({ substitui: a })}>Nova versão</button>
                )}
              </div>
              <p className="agt-artigo-texto">{a.texto}</p>
            </li>
          ))}
        </ul>
      )}
      <JanelaDoArtigo escrita={escrita} aoFechar={() => setEscrita(null)}
        aoGravar={(t) => { setEscrita(null); setOk(`O artigo «${t}» foi gravado.`); void carregar(); }} />
    </Cartao>
  );
}

function AbaLacunas() {
  const { dado, erro, carregar } = useCarga<Lacuna[]>('/comercial/base/lacunas');
  const [escrita, setEscrita] = useState<Escrita>(null);
  const [ok, setOk] = useState<string | null>(null);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando as lacunas" />;
  return (
    <Cartao titulo="Lacunas" frase="As perguntas dos clientes que a base não respondeu, das conversas que você vê. Escrever o artigo fecha a lacuna.">
      <div className="agt-corpo"><RespostaDaAcao erro={null} ok={ok} /></div>
      {!dado.length ? <Vazio>Nenhuma lacuna: a base respondeu tudo o que o agente procurou.</Vazio> : (
        <ul className="agt-linhas">
          {dado.map((l) => (
            <li key={l.id}>
              <div className="agt-linha-cabeca">
                <strong>{l.pergunta}</strong>
                <span className="agt-aux">{dataHora(l.criadaEm)} · <Link to={`/comercial/conversa/${l.conversaId}`}>a conversa</Link></span>
                {l.artigoId
                  ? <span className="agt-aux">Respondida pelo artigo «{l.artigoTitulo}»</span>
                  : <button type="button" className="botao discreto" onClick={() => setEscrita({ lacunas: [l] })}>Escrever o artigo</button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <JanelaDoArtigo escrita={escrita} aoFechar={() => setEscrita(null)}
        aoGravar={(t) => { setEscrita(null); setOk(`O artigo «${t}» foi gravado, e a lacuna fechou.`); void carregar(); }} />
    </Cartao>
  );
}

function JanelaDoArtigo({ escrita, aoFechar, aoGravar }: { escrita: Escrita; aoFechar(): void; aoGravar(titulo: string): void }) {
  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  const [tags, setTags] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  useEffect(() => {
    if (!escrita) return;
    setTitulo(escrita.substitui?.titulo ?? escrita.lacunas?.[0]?.pergunta.slice(0, 200) ?? '');
    setTexto(escrita.substitui?.texto ?? '');
    setTags((escrita.substitui?.tags ?? []).join(', '));
    setErro(null);
    setOcupado(false);
  }, [escrita]);
  const falta = faltaPara('gravar', [
    ...(titulo.trim() ? [] : ['o título']),
    ...(texto.trim() ? [] : ['o texto do artigo']),
  ]);
  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api('/comercial/base', {
        metodo: 'POST',
        corpo: {
          titulo: titulo.trim(), texto: texto.trim(),
          tags: tags.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 20),
          ...(escrita?.substitui ? { substituiId: escrita.substitui.id } : {}),
          ...(escrita?.lacunas?.length ? { lacunas: escrita.lacunas.map((l) => l.id) } : {}),
        },
      });
      aoGravar(titulo.trim());
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Dialogo aberto={escrita !== null} titulo={escrita?.substitui ? 'Nova versão do artigo' : 'Novo artigo'}
      rotulo="Base de conhecimento" aoFechar={aoFechar}>
      <div className="agt-janela">
        {escrita?.lacunas?.length ? <p className="agt-texto aux">Responde: «{escrita.lacunas[0].pergunta}»</p> : null}
        <Campo rotulo="Título" value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} />
        <Area rotulo="Texto" value={texto} rows={8} maxLength={20000} onChange={(e) => setTexto(e.target.value)}
          dica="O agente cita este texto como fonte técnica: escreva como a Testesolo responde." />
        <Campo rotulo="Etiquetas" value={tags} maxLength={400} onChange={(e) => setTags(e.target.value)}
          dica="Separadas por vírgula (ex.: SPT, NBR 6484)." />
        {erro ? <ErroDaApi erro={erro} /> : null}
      </div>
      <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
    </Dialogo>
  );
}
